import { useEffect } from 'react';

const SITE_NAME = 'Moreflex Aviation';
const SITE_URL = 'https://www.moreflexaviation.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/og-image.jpg`;

interface SeoProps {
  /** Full page title, shown as-is in the browser tab and search results. */
  title: string;
  /** 120-160 character meta description shown in search results and social previews. */
  description: string;
  /** Route path, e.g. "/services" or "/services#lounge". Used to build the canonical + OG URL. */
  path: string;
  /** Defaults to a branded 1200x630 image; override for a page-specific image. */
  image?: string;
  /** "website" for most pages, "article" for content-style pages. */
  type?: string;
  /** Set true on utility pages (e.g. sitemap) that shouldn't be indexed. */
  noindex?: boolean;
}

function setMeta(selector: string, attr: 'content', value: string) {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function Seo({ title, description, path, image = DEFAULT_OG_IMAGE, type = 'website', noindex = false }: SeoProps) {
  useEffect(() => {
    const url = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;

    document.title = title;

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow');

    document.head.querySelector('link[rel="canonical"]')?.setAttribute('href', url);

    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);

    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', image);
  }, [title, description, path, image, type, noindex]);

  return null;
}

export default Seo;
